import { useMemo, useCallback, useState } from "react";
import { Document, Page } from "react-pdf";
import { MDSIconButtonSize, MDSIconButtonVariant } from "@/mds/components/buttons";
import { MDSIconKind } from "@/mds/components/icon";
import { MDSSkeleton } from "@/mds/components/loading";
import {
  StyledCarouselArrow,
  StyledCarouselArrowButton,
  StyledCarouselContainer,
  StyledCarouselStrip,
  StyledCarouselThumbnail,
} from "./pdf-page-carousel.styles";

interface PdfPageCarouselProps {
  numPages: number;
  currentPage: number;
  onPageSelect: (page: number) => void;
  pdfSrc: string;
  isVisible: boolean;
}

const MAX_THUMBNAIL_HEIGHT = 64;
/** Fallback width assumes US Letter aspect ratio (8.5 × 11) until a real page reports its size */
const FALLBACK_THUMBNAIL_WIDTH = Math.round(MAX_THUMBNAIL_HEIGHT / (11 / 8.5));
const PAGE_SIZE = 5;

export const PdfPageCarousel = ({
  numPages,
  currentPage,
  onPageSelect,
  pdfSrc,
  isVisible,
}: PdfPageCarouselProps) => {
  const [thumbnailWidth, setThumbnailWidth] = useState(FALLBACK_THUMBNAIL_WIDTH);

  /** Compute actual thumbnail width from the page's intrinsic dimensions, capped at MAX_THUMBNAIL_HEIGHT. */
  const handlePageLoadSuccess = useCallback((page: { width: number; height: number }) => {
    const aspectRatio = page.height / page.width;
    setThumbnailWidth(Math.round(MAX_THUMBNAIL_HEIGHT / aspectRatio));
  }, []);
  const needsPagination = numPages > PAGE_SIZE;

  /** Which "window" of pages to show (0-indexed) */
  const windowIndex = useMemo(() => Math.floor((currentPage - 1) / PAGE_SIZE), [currentPage]);

  const windowStart = windowIndex * PAGE_SIZE + 1;
  const windowEnd = Math.min(windowStart + PAGE_SIZE - 1, numPages);
  const visiblePages = useMemo(
    () => Array.from({ length: windowEnd - windowStart + 1 }, (_, i) => windowStart + i),
    [windowStart, windowEnd]
  );

  const hasPrev = windowStart > 1;
  const hasNext = windowEnd < numPages;

  const handlePrev = useCallback(() => {
    const newStart = Math.max(1, windowStart - PAGE_SIZE);
    onPageSelect(newStart);
  }, [windowStart, onPageSelect]);

  const handleNext = useCallback(() => {
    const newStart = Math.min(numPages, windowStart + PAGE_SIZE);
    onPageSelect(newStart);
  }, [windowStart, numPages, onPageSelect]);

  return (
    <StyledCarouselContainer data-test-id="pdf-carousel" isVisible={isVisible}>
      {needsPagination && (
        <StyledCarouselArrow isHidden={!hasPrev}>
          <StyledCarouselArrowButton
            aria-label="Previous thumbnails"
            iconKind={MDSIconKind.LeftArrow}
            variant={MDSIconButtonVariant.TabBar}
            size={MDSIconButtonSize.Small}
            onClick={handlePrev}
          />
        </StyledCarouselArrow>
      )}

      <StyledCarouselStrip>
        <Document file={pdfSrc} loading={null}>
          {visiblePages.map(pageNum => (
            <StyledCarouselThumbnail
              key={pageNum}
              isActive={pageNum === currentPage}
              data-test-id="pdf-carousel-thumbnail"
              data-active={pageNum === currentPage}
              onClick={() => onPageSelect(pageNum)}
            >
              <Page
                pageNumber={pageNum}
                width={thumbnailWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                onLoadSuccess={handlePageLoadSuccess}
                loading={<MDSSkeleton width={thumbnailWidth} height={MAX_THUMBNAIL_HEIGHT} />}
              />
            </StyledCarouselThumbnail>
          ))}
        </Document>
      </StyledCarouselStrip>

      {needsPagination && (
        <StyledCarouselArrow isHidden={!hasNext}>
          <StyledCarouselArrowButton
            aria-label="Next thumbnails"
            iconKind={MDSIconKind.RightArrow}
            variant={MDSIconButtonVariant.TabBar}
            size={MDSIconButtonSize.Small}
            onClick={handleNext}
          />
        </StyledCarouselArrow>
      )}
    </StyledCarouselContainer>
  );
};
